/**
 * Shared PIN-pad config — the kiosk keypad layout + length and the failed-attempt
 * classifier, used by both the seller login (`pages/sellerLogin`) and the shift
 * discrepancy co-sign (`pages/seller/shift`). Pure: no React, no state.
 * `sellerLoginMeta` re-exports these so its public API stays stable.
 */
import type { ErrorResponse } from '@/api/handlers/responses';
import { PIN_LOCKED, RATE_LIMITED } from '@/utils/apiErrors';

/** Special non-digit keys on the pad. */
export type PinPadAction = 'backspace';

/** Number of digits a PIN must reach before it auto-submits. */
export const PIN_LENGTH = 4;

/** PIN-pad layout: `1`-`9`, a gap, `0`, backspace. `null` is the empty cell. */
export const PIN_PAD_KEYS: (string | PinPadAction | null)[] = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    null,
    '0',
    'backspace',
];

/**
 * How long to wait when a 429 carries no readable `Retry-After`: bos-api's
 * sign-in window is one minute, so a full window is always enough.
 */
export const DEFAULT_RETRY_AFTER_SECONDS = 60;

/**
 * Why a PIN attempt failed, told apart by the server's code (BOS-190), never by
 * its message:
 * - `lockout` — too many wrong PINs; the account is locked for a few minutes;
 * - `rateLimited` — too many attempts in the window; retry after the delay;
 * - `invalid` — anything else, deliberately generic so a probe learns nothing
 *   about which users exist or why exactly a PIN was refused.
 */
export type PinError =
    | { kind: 'lockout' }
    | { kind: 'rateLimited'; retryAfterSeconds: number }
    | { kind: 'invalid' };

export const resolvePinError = (error: ErrorResponse): PinError => {
    if (error.code === PIN_LOCKED) {
        return { kind: 'lockout' };
    }
    if (error.code === RATE_LIMITED || error.status === 429) {
        return {
            kind: 'rateLimited',
            retryAfterSeconds:
                error.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
        };
    }
    return { kind: 'invalid' };
};
